import React, { useState, useCallback, useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { Wrapper } from './styles'
import { getCurrentUser, patchCurrentUser } from 'src/state/users'
import { faCheck, faPlus, faTrash, faDrawPolygon } from '@fortawesome/free-solid-svg-icons'
import Button from 'src/global/Button'
import { HighlightedInfo } from 'src/global'
import { State } from 'src/state'
import { GoogleMap, Polygon, DrawingManager, OverlayView } from '@react-google-maps/api'
import { DEFAULT_LOGO_URL } from 'src/global/Header/TopRightDropdown'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import * as turf from '@turf/turf'
import { DeliveryZone, DeliveryZonesConfig, LatLng, ToggleableOption } from '@meniudigital/shared/consumer-orders-delivery'
import { UserPatchType } from '@meniudigital/shared'
import moment from 'moment'
import { Screen } from 'src/screens'

const ROMANIA_COORDS = { lat: 45.9432, lng: 24.9668 }
const DEFAULT_ZOOM = 7

const generateId = () => Math.random().toString(36).substring(2, 15)

const ZONE_COLORS = [
  '#1ea750', // green
  '#3b82f6', // blue
  '#f97316', // orange
  '#14b8a6', // teal
  '#eab308', // yellow
  '#ec4899', // pink
  '#8b5cf6', // purple
  '#ef4444', // red
]

const getZoneColor = (index: number) => {
  if (index >= ZONE_COLORS.length) {
    return '#ef4444' // red for overflow
  }
  return ZONE_COLORS[index]
}

const SNAP_DISTANCE_METERS = 100
const DELETE_DISTANCE_METERS = 15

const toTurfPoint = (point: LatLng) => turf.point([point.lng, point.lat])

type SnapTarget =
  | { type: 'vertex'; zoneId: string; vertexIndex: number; snappedPoint: LatLng }
  | { type: 'edge'; zoneId: string; fromIndex: number; toIndex: number; snappedPoint: LatLng }

const findSnapTarget = (point: LatLng, zones: DeliveryZone[], excludeZoneId?: string): SnapTarget | null => {
  const pointFeature = toTurfPoint(point)
  let bestVertex: SnapTarget | null = null
  let bestVertexDistance = Number.POSITIVE_INFINITY
  let bestEdge: SnapTarget | null = null
  let bestEdgeDistance = Number.POSITIVE_INFINITY

  zones.forEach(zone => {
    if (zone.id === excludeZoneId || zone.polygon.length < 2) {
      return
    }

    zone.polygon.forEach((coord, index) => {
      const distance = turf.distance(pointFeature, toTurfPoint(coord), { units: 'meters' })
      if (distance <= SNAP_DISTANCE_METERS && distance < bestVertexDistance) {
        bestVertex = { type: 'vertex', zoneId: zone.id, vertexIndex: index, snappedPoint: coord }
        bestVertexDistance = distance
      }

      const next = zone.polygon[(index + 1) % zone.polygon.length]
      const line = turf.lineString([
        [coord.lng, coord.lat],
        [next.lng, next.lat],
      ])
      const nearestPoint = turf.nearestPointOnLine(line, pointFeature)
      const snappedPoint = { lat: nearestPoint.geometry.coordinates[1], lng: nearestPoint.geometry.coordinates[0] }
      const edgeDistance = turf.distance(pointFeature, toTurfPoint(snappedPoint), { units: 'meters' })

      if (edgeDistance <= SNAP_DISTANCE_METERS && edgeDistance < bestEdgeDistance) {
        bestEdge = {
          type: 'edge',
          zoneId: zone.id,
          fromIndex: index,
          toIndex: (index + 1) % zone.polygon.length,
          snappedPoint,
        }
        bestEdgeDistance = edgeDistance
      }
    })
  })

  if (bestVertex) {
    return bestVertex
  }

  return bestEdge
}

const snapPolygon = (
  polygon: LatLng[],
  zones: DeliveryZone[],
  excludeZoneId?: string,
): { polygon: LatLng[]; metadata: (SnapTarget | null)[] } => {
  const snapped: LatLng[] = []
  const metadata: (SnapTarget | null)[] = []

  polygon.forEach(coord => {
    const target = findSnapTarget(coord, zones, excludeZoneId)
    if (target) {
      snapped.push(target.snappedPoint)
      metadata.push(target)
    } else {
      snapped.push(coord)
      metadata.push(null)
    }
  })

  return { polygon: snapped, metadata }
}

const getShortestPathBetweenVertices = (polygon: LatLng[], startIndex: number, endIndex: number): LatLng[] => {
  if (polygon.length === 0) {
    return []
  }

  const forwardPath: LatLng[] = []
  let index = startIndex
  while (true) {
    forwardPath.push(polygon[index])
    if (index === endIndex) {
      break
    }
    index = (index + 1) % polygon.length
  }

  const backwardPath: LatLng[] = []
  index = startIndex
  while (true) {
    backwardPath.push(polygon[index])
    if (index === endIndex) {
      break
    }
    index = (index - 1 + polygon.length) % polygon.length
  }

  return forwardPath.length <= backwardPath.length ? forwardPath : backwardPath
}

const mergeSharedEdges = (polygon: LatLng[], metadata: (SnapTarget | null)[], zones: DeliveryZone[]): LatLng[] => {
  if (polygon.length < 2) {
    return polygon
  }

  const merged: LatLng[] = []

  for (let i = 0; i < polygon.length; i++) {
    const current = polygon[i]
    const nextIndex = (i + 1) % polygon.length
    const next = polygon[nextIndex]
    merged.push(current)

    const currentMeta = metadata[i]
    const nextMeta = metadata[nextIndex]

    if (
      currentMeta &&
      nextMeta &&
      currentMeta.type === 'vertex' &&
      nextMeta.type === 'vertex' &&
      currentMeta.zoneId === nextMeta.zoneId &&
      currentMeta.vertexIndex !== nextMeta.vertexIndex
    ) {
      const sharedZone = zones.find(z => z.id === currentMeta.zoneId)
      if (!sharedZone) {
        continue
      }

      const sharedPath = getShortestPathBetweenVertices(sharedZone.polygon, currentMeta.vertexIndex, nextMeta.vertexIndex)
      if (sharedPath.length > 2) {
        sharedPath.slice(1, sharedPath.length - 1).forEach(point => merged.push(point))
      } else if (sharedPath.length === 2) {
        const first = sharedPath[0]
        const last = sharedPath[1]
        if (first.lat === current.lat && first.lng === current.lng && last.lat === next.lat && last.lng === next.lng) {
          continue
        }
      }
    }
  }

  return merged
}

const removeDuplicateSequentialPoints = (polygon: LatLng[]): LatLng[] => {
  if (polygon.length === 0) {
    return polygon
  }

  const cleaned: LatLng[] = [polygon[0]]

  for (let i = 1; i < polygon.length; i++) {
    const prev = cleaned[cleaned.length - 1]
    const curr = polygon[i]
    if (prev.lat !== curr.lat || prev.lng !== curr.lng) {
      cleaned.push(curr)
    }
  }

  return cleaned
}

const findClosestVertexIndex = (polygon: LatLng[], point: LatLng) => {
  const hasGoogleGeometry = typeof google !== 'undefined' && google?.maps?.geometry?.spherical?.computeDistanceBetween
  let bestIndex = -1
  let bestDistance = Number.POSITIVE_INFINITY

  polygon.forEach((coord, index) => {
    let distance = Number.POSITIVE_INFINITY
    if (hasGoogleGeometry) {
      distance = google.maps.geometry.spherical.computeDistanceBetween(
        new google.maps.LatLng(coord.lat, coord.lng),
        new google.maps.LatLng(point.lat, point.lng),
      )
    } else {
      distance = turf.distance(toTurfPoint(coord), toTurfPoint(point), { units: 'meters' }) * 1000
    }
    if (distance < bestDistance) {
      bestDistance = distance
      bestIndex = index
    }
  })

  return { index: bestIndex, distance: bestDistance }
}

const validateZones = (_: DeliveryZone[]): { isValid: boolean; error?: string } => {
  return { isValid: true }
}

const generateCircularPolygon = (center: LatLng, radiusKm: number, numPoints: number): LatLng[] => {
  const points: LatLng[] = []
  const centerPoint = turf.point([center.lng, center.lat])

  for (let i = 0; i < numPoints; i++) {
    const angle = (i / numPoints) * 360
    const destination = turf.destination(centerPoint, radiusKm, angle, { units: 'kilometers' })
    points.push({
      lat: destination.geometry.coordinates[1],
      lng: destination.geometry.coordinates[0],
    })
  }

  return points
}

const getZoneArea = (zone: DeliveryZone) => {
  if (zone.polygon.length < 3) {
    return Number.POSITIVE_INFINITY
  }

  const coords = [...zone.polygon.map(p => [p.lng, p.lat]), [zone.polygon[0].lng, zone.polygon[0].lat]]
  const turfPoly = turf.polygon([coords])
  return turf.area(turfPoly)
}

export default function SetDeliveryZones() {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { user } = useSelector((state: State) => state.users)

  const [zones, setZones] = useState<DeliveryZone[]>([])
  const [savedZones, setSavedZones] = useState<DeliveryZone[]>([])
  const [selectedZoneId, setSelectedZoneId] = useState<string | null>(null)
  const [isDrawing, setIsDrawing] = useState(false)
  const [validationError, setValidationError] = useState<string | null>(null)
  const [isSaving, setIsSaving] = useState(false)

  const polygonRefs = useRef<Map<string, google.maps.Polygon>>(new Map())
  const previousValidZones = useRef<DeliveryZone[]>([])
  const mapRef = useRef<google.maps.Map | null>(null)
  const [isMobile, setIsMobile] = useState(() => (typeof window !== 'undefined' ? window.innerWidth <= 768 : false))

  const isDirty = JSON.stringify(zones) !== JSON.stringify(savedZones)

  useEffect(() => {
    dispatch(getCurrentUser())
  }, [dispatch])

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isDrawing) {
        setIsDrawing(false)
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [isDrawing])

  useEffect(() => {
    const onResize = () => {
      setIsMobile(window.innerWidth <= 768)
    }
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  useEffect(() => {
    if (isMobile && isDrawing) {
      setIsDrawing(false)
    }
  }, [isMobile, isDrawing])

  useEffect(() => {
    if (user?.ordering?.deliveryZonesConfig?.zones) {
      setZones(user.ordering.deliveryZonesConfig.zones)
      setSavedZones(user.ordering.deliveryZonesConfig.zones)
      previousValidZones.current = user.ordering.deliveryZonesConfig.zones
    }
  }, [user?.ordering?.deliveryZonesConfig])

  useEffect(() => {
    if (!user || zones.length > 0 || savedZones.length > 0) {
      return
    }

    const hasCoords = Boolean(user.coords?.latitude && user.coords?.longitude)
    if (!hasCoords) {
      return
    }

    const center = { lat: user.coords!.latitude, lng: user.coords!.longitude }

    const innerZone: DeliveryZone = {
      id: generateId(),
      name: 'În Apropiere',
      isActive: true,
      polygon: generateCircularPolygon(center, 1, 10),
      deliveryFee: 0,
    }

    const outerZone: DeliveryZone = {
      id: generateId(),
      name: 'Restul Orașului',
      isActive: true,
      polygon: generateCircularPolygon(center, 3, 20),
      deliveryFee: 20,
    }

    setZones([innerZone, outerZone])
  }, [user, zones.length, savedZones.length])

  useEffect(() => {
    if (!user) {
      return
    }

    const hasCoords = Boolean(user.coords?.latitude && user.coords?.longitude)
    if (!hasCoords) {
      navigate(`${Screen.SetLocation}?returnTo=${Screen.SetDeliveryZones}`)
    }
  }, [navigate, user])

  useEffect(() => {
    const validation = validateZones(zones)
    if (!validation.isValid) {
      setValidationError(validation.error || null)
    } else {
      setValidationError(null)
      previousValidZones.current = zones
    }
  }, [zones])

  const hasCoords = Boolean(user?.coords?.latitude && user?.coords?.longitude)
  const initialCoords = hasCoords ? { lat: user!.coords!.latitude, lng: user!.coords!.longitude } : ROMANIA_COORDS

  const onPolygonComplete = useCallback(
    (polygon: google.maps.Polygon) => {
      const path = polygon.getPath()
      let coords: LatLng[] = []
      const newZoneId = generateId()

      for (let i = 0; i < path.getLength(); i++) {
        const point = path.getAt(i)
        coords.push({ lat: point.lat(), lng: point.lng() })
      }

      const firstSnapTarget = findSnapTarget(coords[0], zones)
      if (firstSnapTarget) {
        coords[0] = firstSnapTarget.snappedPoint
      }

      const { polygon: snappedPolygon, metadata } = snapPolygon(coords, zones)
      const mergedPolygon = mergeSharedEdges(snappedPolygon, metadata, zones)
      coords = removeDuplicateSequentialPoints(mergedPolygon)

      if (coords.length < 3) {
        polygon.setMap(null)
        setIsDrawing(false)
        return
      }

      const newZone: DeliveryZone = {
        id: newZoneId,
        name: `Zona ${zones.length + 1}`,
        isActive: true,
        polygon: coords,
        deliveryFee: 0,
      }

      const newZones = [...zones, newZone]
      const validation = validateZones(newZones)

      if (!validation.isValid) {
        polygon.setMap(null)
        window.showConfirm({
          title: 'Zonă invalidă',
          text: validation.error || 'Zona se suprapune cu o zonă existentă',
        })
      } else {
        setZones(newZones)
        setSelectedZoneId(newZone.id)
      }

      polygon.setMap(null)
      setIsDrawing(false)
    },
    [zones],
  )

  const updateZonePolygon = useCallback(
    (zoneId: string, newPolygon: LatLng[]) => {
      const revertToPreviousPolygon = () => {
        const previousZone = previousValidZones.current.find(z => z.id === zoneId)
        if (previousZone) {
          const polygonRef = polygonRefs.current.get(zoneId)
          if (polygonRef) {
            const path = polygonRef.getPath()
            path.clear()
            previousZone.polygon.forEach(coord => {
              path.push(new google.maps.LatLng(coord.lat, coord.lng))
            })
          }
        }
      }

      const { polygon: snappedPolygon, metadata } = snapPolygon(newPolygon, zones, zoneId)
      const mergedPolygon = mergeSharedEdges(snappedPolygon, metadata, zones)
      const cleanedPolygon = removeDuplicateSequentialPoints(mergedPolygon)

      if (cleanedPolygon.length < 3) {
        setValidationError('Poligonul are prea puține puncte după ajustarea marginilor comune.')
        revertToPreviousPolygon()
        return
      }

      const newZones = zones.map(z => (z.id === zoneId ? { ...z, polygon: cleanedPolygon } : z))

      const validation = validateZones(newZones)

      if (!validation.isValid) {
        setValidationError(validation.error || null)
        revertToPreviousPolygon()
        return
      }

      setZones(newZones)
    },
    [zones],
  )

  const deleteVertexIfClose = useCallback(
    (zone: DeliveryZone, clickPoint: LatLng, vertexIndexFromEvent?: number | null) => {
      if (zone.polygon.length <= 3) {
        return
      }

      const polygonRef = polygonRefs.current.get(zone.id)
      const currentCoords: LatLng[] = polygonRef
        ? polygonRef
            .getPath()
            .getArray()
            .map(p => ({ lat: p.lat(), lng: p.lng() }))
        : zone.polygon

      const indexToRemove =
        typeof vertexIndexFromEvent === 'number' && vertexIndexFromEvent >= 0 && vertexIndexFromEvent < currentCoords.length
          ? vertexIndexFromEvent
          : findClosestVertexIndex(currentCoords, clickPoint).index

      if (indexToRemove === -1) {
        return
      }

      const { distance } = findClosestVertexIndex([currentCoords[indexToRemove]], clickPoint)
      if (distance > DELETE_DISTANCE_METERS) {
        return
      }

      const newPolygon = currentCoords.filter((_, i) => i !== indexToRemove)
      updateZonePolygon(zone.id, newPolygon)
    },
    [updateZonePolygon],
  )

  const updateZoneField = <K extends keyof DeliveryZone>(zoneId: string, field: K, value: DeliveryZone[K]) => {
    setZones(zones.map(z => (z.id === zoneId ? { ...z, [field]: value } : z)))
  }

  const updateToggleableOption = (
    zoneId: string,
    field: 'minimumOrder' | 'freeDeliveryFrom',
    update: Partial<ToggleableOption>,
  ) => {
    setZones(
      zones.map(z => {
        if (z.id !== zoneId) {
          return z
        }
        const current = z[field] || { isEnabled: false, value: 0 }
        return { ...z, [field]: { ...current, ...update } }
      }),
    )
  }

  const deleteZone = (zoneId: string) => {
    setZones(zones.filter(z => z.id !== zoneId))
    if (selectedZoneId === zoneId) {
      setSelectedZoneId(null)
    }
    polygonRefs.current.delete(zoneId)
  }

  const confirmDeleteZone = async (zone: DeliveryZone) => {
    try {
      await window.showModal({
        title: 'Șterge zona',
        text: `Sigur dorești să ștergi zona "${zone.name}"?`,
        confirmButtonText: 'Șterge',
      })
      deleteZone(zone.id)
    } catch {
      // User cancelled
    }
  }

  const save = async () => {
    if (validationError || !isDirty) {
      return
    }

    setIsSaving(true)

    const config: DeliveryZonesConfig = {
      updatedAt: moment().toISOString(),
      zones,
    }

    try {
      await dispatch(
        patchCurrentUser({
          type: UserPatchType.DeliveryZonesConfig,
          data: config,
        }),
      )

      setSavedZones(zones)
      window.showConfirm({ text: 'Zonele de livrare au fost salvate cu succes.' })
    } finally {
      setIsSaving(false)
    }
  }

  const onPolygonLoad = useCallback((polygon: google.maps.Polygon, zoneId: string) => {
    polygonRefs.current.set(zoneId, polygon)
  }, [])

  const onPolygonEdit = useCallback(
    (zoneId: string) => {
      const polygon = polygonRefs.current.get(zoneId)
      if (!polygon) {
        return
      }

      const path = polygon.getPath()
      const newCoords: LatLng[] = []

      for (let i = 0; i < path.getLength(); i++) {
        const point = path.getAt(i)
        newCoords.push({ lat: point.lat(), lng: point.lng() })
      }

      updateZonePolygon(zoneId, newCoords)
    },
    [updateZonePolygon],
  )

  if (!user) {
    return null
  }

  const zonesWithFeeOrder = zones
    .map((zone, originalIndex) => ({
      zone,
      originalIndex,
      fee: zone.deliveryFee ?? 0,
    }))
    .sort((a, b) => a.fee - b.fee)

  const feeIndexByZoneId = new Map<string, number>()
  zonesWithFeeOrder.forEach(({ zone }, idx) => feeIndexByZoneId.set(zone.id, idx))

  const getColorForZone = (zoneId: string, fallbackIndex: number) => {
    const idx = feeIndexByZoneId.get(zoneId)
    return getZoneColor(typeof idx === 'number' ? idx : fallbackIndex)
  }

  const zonesByArea = zones
    .map(zone => ({
      zoneId: zone.id,
      area: getZoneArea(zone),
    }))
    .sort((a, b) => a.area - b.area)

  const areaRankByZoneId = new Map<string, number>()
  zonesByArea.forEach((entry, idx) => areaRankByZoneId.set(entry.zoneId, idx))

  const zIndexForZone = (zoneId: string) => {
    const rank = areaRankByZoneId.get(zoneId)
    return 1000 + (zonesByArea.length - (typeof rank === 'number' ? rank : 0))
  }

  return (
    <Wrapper>
      <div className="layout">
        <div className="sidebar">
          {isMobile ? (
            <HighlightedInfo
              style={{ borderRadius: 0, marginTop: 0 }}
              text="Accesează pe desktop pentru a desena și modifica zonele pe hartă."
            />
          ) : !isDrawing ? (
            <div className="info-banner">
              <p>Editează/adaugă/redenumește zonele în care livrezi și taxele aferente acestora.</p>
              <p>- Click pe o zonă pentru a o edita sau adăuga puncte noi.</p>
              <p>- Clienții din afara acestor zone nu vor putea plasa comenzi cu livrare.</p>
            </div>
          ) : null}

          {isDrawing && (
            <div className="drawing-explainer">
              <div className="drawing-explainer-header">
                <FontAwesomeIcon icon={faDrawPolygon} />
                <h4>Desenează zona</h4>
              </div>
              <span>Fă click pe hartă pentru a adăuga puncte și a închide poligonul pe primul punct.</span>
              <span>După finalizare, click dreapta pe un punct pentru a-l șterge.</span>
              <Button text="Anulează" variant="secondary" onClick={() => setIsDrawing(false)} />
            </div>
          )}

          {validationError && <div className="validation-error">{validationError}</div>}

          <div className="zones-list">
            {zones.length === 0 && !isDrawing && (
              <div className="empty-state">
                <p>Nu ai nicio zonă de livrare definită.</p>
                <p>Apasă "Zonă nouă" pentru a adăuga una.</p>
              </div>
            )}

            {zones.map((zone, index) => (
              <div
                key={zone.id}
                className="zone-card"
                style={{ ['--zone-color' as string]: getColorForZone(zone.id, index) }}
                onClick={() => setSelectedZoneId(zone.id)}
              >
                <div className="zone-header">
                  <div className="zone-color-bar" />
                  <input
                    type="text"
                    className="zone-name-input"
                    value={zone.name}
                    autoFocus
                    onChange={e => updateZoneField(zone.id, 'name', e.target.value)}
                    onClick={e => e.stopPropagation()}
                    onFocus={e => e.target.select()}
                    placeholder="Nume zonă"
                  />
                  <div
                    className="delete-btn"
                    onClick={e => {
                      e.stopPropagation()
                      confirmDeleteZone(zone)
                    }}
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </div>
                </div>

                <div className="zone-settings">
                  <div className="setting-row">
                    <span className="setting-label">Cost livrare</span>
                    <div className="setting-input">
                      <input
                        type="number"
                        value={zone.deliveryFee}
                        onChange={e => updateZoneField(zone.id, 'deliveryFee', parseFloat(e.target.value) || 0)}
                        onClick={e => e.stopPropagation()}
                        onFocus={e => e.target.select()}
                        min={0}
                        step={0.5}
                      />
                      <span className="unit">lei</span>
                    </div>
                  </div>

                  <div className="setting-row">
                    <div
                      className={`toggle ${zone.minimumOrder?.isEnabled ? 'on' : ''}`}
                      onClick={e => {
                        e.stopPropagation()
                        updateToggleableOption(zone.id, 'minimumOrder', {
                          isEnabled: !zone.minimumOrder?.isEnabled,
                        })
                      }}
                    >
                      <div className="toggle-track" />
                      <div className="toggle-knob" />
                    </div>
                    <span className="setting-label">Comandă minimă</span>
                    {zone.minimumOrder?.isEnabled && (
                      <div className="setting-input">
                        <input
                          type="number"
                          value={zone.minimumOrder?.value ?? 0}
                          onChange={e =>
                            updateToggleableOption(zone.id, 'minimumOrder', {
                              value: parseFloat(e.target.value) || 0,
                            })
                          }
                          onClick={e => e.stopPropagation()}
                          onFocus={e => e.target.select()}
                          min={0}
                          step={1}
                        />
                        <span className="unit">lei</span>
                      </div>
                    )}
                  </div>

                  <div className="setting-row">
                    <div
                      className={`toggle ${zone.freeDeliveryFrom?.isEnabled ? 'on' : ''}`}
                      onClick={e => {
                        e.stopPropagation()
                        updateToggleableOption(zone.id, 'freeDeliveryFrom', {
                          isEnabled: !zone.freeDeliveryFrom?.isEnabled,
                        })
                      }}
                    >
                      <div className="toggle-track" />
                      <div className="toggle-knob" />
                    </div>
                    <span className="setting-label">Livrare gratuită de la</span>
                    {zone.freeDeliveryFrom?.isEnabled && (
                      <div className="setting-input">
                        <input
                          type="number"
                          value={zone.freeDeliveryFrom?.value ?? 0}
                          onChange={e =>
                            updateToggleableOption(zone.id, 'freeDeliveryFrom', {
                              value: parseFloat(e.target.value) || 0,
                            })
                          }
                          onClick={e => e.stopPropagation()}
                          onFocus={e => e.target.select()}
                          min={0}
                          step={1}
                        />
                        <span className="unit">lei</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
            {!isDrawing && (
              <div style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                <Button
                  icon={faPlus}
                  disabled={isDrawing || isMobile}
                  text="Zonă nouă"
                  variant="primary"
                  onClick={() => {
                    if (!isMobile) {
                      setIsDrawing(true)
                    }
                  }}
                  style={{ alignSelf: 'center' }}
                />
              </div>
            )}
          </div>

          <div className="sidebar-footer">
            <Button
              icon={faCheck}
              text="Salvează"
              onClick={save}
              disabled={!!validationError || isSaving || !isDirty}
              isLoading={isSaving}
            />
            {isDirty && <Button variant="as-text" text="Renunță" onClick={() => navigate(Screen.ConsumerOrders)} />}
          </div>
        </div>

        {!isMobile && (
          <div className="map-container">
            <GoogleMap
              mapContainerClassName="google-map"
              onLoad={map => {
                mapRef.current = map
                map.setCenter(initialCoords)
                map.setZoom(hasCoords ? 14 : DEFAULT_ZOOM)
              }}
              onClick={() => {
                if (!isDrawing) {
                  setSelectedZoneId(null)
                }
              }}
              options={{
                mapTypeControl: false,
                fullscreenControl: true,
                streetViewControl: false,
              }}
            >
              {isDrawing && (
                <DrawingManager
                  onPolygonComplete={onPolygonComplete}
                  options={{
                    drawingMode: 'polygon' as google.maps.drawing.OverlayType,
                    drawingControl: false,
                    polygonOptions: {
                      fillColor: getZoneColor(zones.length),
                      fillOpacity: 0.3,
                      strokeColor: getZoneColor(zones.length),
                      strokeWeight: 6,
                      editable: true,
                      draggable: false,
                      zIndex: 5000,
                      clickable: false,
                    },
                  }}
                />
              )}

              {zonesWithFeeOrder.map(({ zone, originalIndex }) => (
                <Polygon
                  key={zone.id}
                  paths={zone.polygon}
                  options={{
                    fillColor: getColorForZone(zone.id, originalIndex),
                    fillOpacity: selectedZoneId === zone.id ? 0.35 : 0.12,
                    strokeColor: getColorForZone(zone.id, originalIndex),
                    strokeWeight: selectedZoneId === zone.id ? 4 : 2,
                    editable: selectedZoneId === zone.id && !isDrawing,
                    draggable: false,
                    clickable: !isDrawing,
                    zIndex: zIndexForZone(zone.id),
                  }}
                  onClick={
                    !isDrawing
                      ? () => {
                          setSelectedZoneId(zone.id)
                        }
                      : undefined
                  }
                  onRightClick={
                    !isDrawing
                      ? e => {
                          if (e.latLng) {
                            deleteVertexIfClose(
                              zone,
                              { lat: e.latLng.lat(), lng: e.latLng.lng() },
                              (e as google.maps.PolyMouseEvent).vertex,
                            )
                          }
                        }
                      : undefined
                  }
                  onLoad={polygon => onPolygonLoad(polygon, zone.id)}
                  onMouseUp={() => onPolygonEdit(zone.id)}
                  onDragEnd={() => onPolygonEdit(zone.id)}
                />
              ))}

              {isDrawing &&
                zonesWithFeeOrder.map(({ zone, originalIndex }) =>
                  zone.polygon.map((point, pointIndex) => (
                    <OverlayView
                      key={`${zone.id}-drawing-vertex-${pointIndex}`}
                      position={{ lat: point.lat, lng: point.lng }}
                      mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                    >
                      <div
                        className="vertex-marker"
                        style={{
                          borderColor: getColorForZone(zone.id, originalIndex),
                          transform: 'translate(-50%, -50%) scale(0.7)',
                        }}
                      />
                    </OverlayView>
                  )),
                )}

              {hasCoords && (
                <OverlayView position={initialCoords} mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}>
                  <div className="restaurant-marker">
                    <img loading="lazy" src={user.logoUrl || DEFAULT_LOGO_URL} alt="Restaurant" className="restaurant-logo" />
                  </div>
                </OverlayView>
              )}

              {zonesWithFeeOrder.map(({ zone, originalIndex }) => {
                if (zone.polygon.length < 3) {
                  return null
                }
                const coords = [...zone.polygon.map(p => [p.lng, p.lat]), [zone.polygon[0].lng, zone.polygon[0].lat]]
                const turfPoly = turf.polygon([coords])
                const centroid = turf.centroid(turfPoly).geometry?.coordinates
                if (!centroid) {
                  return null
                }

                const lats = zone.polygon.map(p => p.lat)
                const minLat = Math.min(...lats)
                const labelPosition = { lat: minLat + 0.012, lng: centroid[0] }
                return (
                  <OverlayView key={`${zone.id}-label`} position={labelPosition} mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}>
                    <div
                      className="zone-label"
                      style={{
                        ['--label-color' as string]: getColorForZone(zone.id, originalIndex),
                      }}
                    >
                      <div className="zone-label-name">{zone.name || `Zona ${originalIndex + 1}`}</div>
                      <div className="zone-label-info">
                        {zone.minimumOrder?.isEnabled ? (
                          <span className="zone-label-detail min">Comandă min. {zone.minimumOrder.value} lei</span>
                        ) : (
                          <span className="zone-label-detail">Fără comandă minimă</span>
                        )}
                        <span className="zone-label-fee">
                          {zone.deliveryFee ? `Livrare: ${zone.deliveryFee} lei` : 'Livrare gratuită'}
                        </span>
                        {zone.freeDeliveryFrom?.isEnabled && (
                          <span className="zone-label-detail free">Gratuită peste {zone.freeDeliveryFrom.value} lei</span>
                        )}
                      </div>
                    </div>
                  </OverlayView>
                )
              })}
            </GoogleMap>
          </div>
        )}
      </div>
    </Wrapper>
  )
}
